import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Modal, Textinput, Typo, Flex, SimpleButton, theme } from '../../../ui'; // Added Alert
import { useInvitesModalState } from '../../invites/hooks/useInvitesState';
import { useInvitesActions } from '../../invites/hooks/useInvitesActions';
import { CSVLink } from '../../createCsv';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import CheckboxButton from '../../../ui/CheckboxButton';
import { useFeatures } from '../../settings/hooks';
import PageControls from '../../../ui/Page/PageControls';

const GenerateGiftCodesModal = ({ course, onClose, isAdmin }) => {
  const { t } = useTranslation();

  const features = useFeatures();
  const { modalState } = useInvitesModalState();
  const status = modalState?.status || 'idle';
  const giftCodeError = modalState?.giftCodeError || null;

  const { generateGiftCodes } = useInvitesActions();

  const amountRef = useRef(null);
  const idRef = useRef(null);
  const yearRef = useRef(null);

  const [exportData, setExportData] = useState(null);
  const [isCoursePackage, setIsCoursePackage] = useState(false);

  // Clear export data and error when modal is opened/closed or course changes
  useEffect(() => {
    setExportData(null);
  }, [course, onClose]);

  const handleCreateGiftCodes = useCallback(async () => {
    setExportData(null); // Clear previous export data
    const payload = {
      amount: amountRef.current.value,
      objectType: isCoursePackage ? 'package' : 'course',
      courseId: idRef?.current?.value || course?.id,
    };
    if (yearRef?.current?.value) {
      payload.year = parseInt(yearRef.current.value, 10); // Ensure year is an integer
    }

    const result = await generateGiftCodes(payload);
    const codes = result.codes || result;
    const data = codes.map((item) => ({
      codes: item,
      expire: result.expiry,
    }));
    setExportData(data);
  }, [generateGiftCodes, course, setExportData, isCoursePackage, idRef, amountRef, yearRef]);

  const toggleCoursePackage = (e) => {
    setIsCoursePackage(e.target.checked);
  };

  return (
    <Modal open={!!course} onClose={onClose} title={course?.name || t('course.typeYourCourse')} width='650px'>
      <Flex direction='column' gap='10' width='100%'>
        {isAdmin && !course?.id && (
          <Flex direction='column' width='100%' align='flex-start'>
            <Typo.Text>course id</Typo.Text>
            <Textinput type='number' inputRef={idRef} margin='dense' id='courseId' fullWidth />
            <CheckboxButton labelText={t('course.courseIsPackage')} checked={isCoursePackage} handleChange={toggleCoursePackage} />
          </Flex>
        )}
        <Typo.Text>{t('course.howManyCodes')}</Typo.Text>
        <Textinput autoFocus type='number' inputRef={amountRef} margin='dense' id='giftCodes' fullWidth disabled={status === 'loading'} />
        {(features?.giftCodeExpiration || isAdmin) && (
          <>
            <Typo.Text>{t('course.validationTimeSpanInYears')}</Typo.Text>
            <Textinput type='number' inputRef={yearRef} margin='dense' id='validationYears' fullWidth disabled={status === 'loading'} />
          </>
        )}
      </Flex>

      <SimpleButton startIcon='Sync' onClick={handleCreateGiftCodes} loadingText={t('globals.loading')} isLoading={status === 'loading'}>
        {t('course.createGiftCodes')}
      </SimpleButton>
      <PageControls
        openModelButton={
          <>
            {giftCodeError && (
              <Typo.SmallText color={theme.colors.ERROR_RED} margin='10px 0'>
                {giftCodeError}
              </Typo.SmallText>
            )}
            {exportData?.length && !giftCodeError && (
              <CSVLink data={exportData} filename={`${t('course.giftCodes')} ${course?.name || idRef?.current?.value} ${dayjs().format('YYYY-MM-DD')}.csv`}>
                {t('course.downloadFile')}
              </CSVLink>
            )}
          </>
        }
      />
    </Modal>
  );
};

export default GenerateGiftCodesModal;
